import { useSearchParams } from "react-router-dom";
import { CTASection, SectionHeading } from "@/components/sections";
import { Reveal, Stagger, StaggerItem } from "@/components/primitives";
import { ProductHero, RequestInfo } from "@/components/product-parts";
import { Gauge, Waves, MonitorSmartphone, Split, Check, Package } from "lucide-react";
import { IMG } from "@/lib/site";

const SPECS = [
  { icon: Split, label: "4 uscite", value: "Trattamento sequenziale su più camere" },
  { icon: Gauge, label: "20–250 mmHg", value: "Ampio range di pressione regolabile" },
  { icon: MonitorSmartphone, label: "Touch screen", value: "Interfaccia digitale intuitiva" },
  { icon: Waves, label: "Programmi A e B", value: "Cicli sequenziali personalizzabili" },
];

// Accessori del kit organizzati per "camera" (gruppo). Ogni accessorio è a 4 sezioni.
const ACCESSORI_GROUPS = [
  {
    id: "bracciali",
    label: "Bracciali",
    items: [
      { code: "LTM563-S", desc: "Bracciale 4 sezioni, completo di tubi — taglia S/M" },
      { code: "LTM563-L", desc: "Bracciale 4 sezioni, completo di tubi — taglia L/XL" },
    ],
  },
  {
    id: "gambali",
    label: "Gambali",
    items: [
      { code: "LTM566-S", desc: "Gambale 4 sezioni, completo di tubi — taglia S/M" },
      { code: "LTM566-L", desc: "Gambale 4 sezioni, completo di tubi — taglia L/XL" },
      { code: "LTM566-XXL", desc: "Gambale 4 sezioni, completo di tubi — taglia XXL" },
      { code: "LTM566-3XL", desc: "Gambale 4 sezioni, completo di tubi — taglia XXXL" },
    ],
  },
  {
    id: "addominale",
    label: "Fascia addominale",
    items: [
      { code: "LTM569-S", desc: "Fascia addominale 4 sezioni, completo di tubi — taglia S/M" },
      { code: "LTM569-L", desc: "Fascia addominale 4 sezioni, completo di tubi — taglia L/XL" },
    ],
  },
  {
    id: "optional-ricambi",
    label: "Optional e ricambi",
    items: [
      { code: "LTM571", desc: "Tubo a 4 vie doppio per gambali o bracciali" },
      { code: "LTM568", desc: "Estensione per gambale 10 cm (LTM566-L, LTM566-3XL)" },
      { code: "LTM570", desc: "Tubo a 4 vie per bracciale o gambale" },
      { code: "LTM572", desc: "Tubo a 4 vie per fascia addominale" },
      { code: "LTR590", desc: "Tappi colorati per tubo a 4 vie" },
    ],
  },
];

const SPECIFICHE = [
  { k: "Alimentazione", v: "220V / 50Hz" },
  { k: "Pressione", v: "20–250 mmHg" },
  { k: "Uscita d'aria", v: "Modulabile" },
  { k: "Durata ciclo", v: "10 – 20 – 30 minuti" },
  { k: "Cavo alimentazione", v: "1,9 m" },
  { k: "Uscite", v: "4" },
];

const INDICAZIONI = [
  "Linfedema primario e secondario",
  "Insufficienza venosa cronica",
  "Edema post-traumatico e post-chirurgico",
  "Lipedema",
  "Prevenzione del ristagno linfatico",
  "Recupero e benessere degli arti inferiori",
];

const CodeList = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((a) => (
      <li key={a.code} className="flex flex-col gap-0.5 rounded-xl border border-[#E7EDF2] bg-white px-4 py-3 sm:flex-row sm:items-center sm:gap-3" data-testid={`presso-acc-${a.code.toLowerCase()}`}>
        <span className="inline-flex w-fit shrink-0 items-center rounded-md bg-navy px-2.5 py-1 font-mono text-xs font-bold text-white">{a.code}</span>
        <span className="text-sm text-navy/80">{a.desc}</span>
      </li>
    ))}
  </ul>
);

const AccessoriSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get("acc") || "tutti";
  const active = ACCESSORI_GROUPS.some((g) => g.id === raw) ? raw : "tutti";
  const visible = active === "tutti" ? ACCESSORI_GROUPS : ACCESSORI_GROUPS.filter((g) => g.id === active);

  const chips = [{ id: "tutti", label: "Tutti gli accessori" }, ...ACCESSORI_GROUPS.map((g) => ({ id: g.id, label: g.label }))];

  const selectAcc = (id) => {
    if (id === "tutti") setSearchParams({});
    else setSearchParams({ acc: id });
    requestAnimationFrame(() => {
      const el = document.getElementById("presso-kit");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="presso-accessori">
      <div className="container-x">
        <SectionHeading size="cat" eyebrow="Composizione del kit" title="Pompa e accessori." />
        <div className="mt-4 flex max-w-2xl items-start gap-3 rounded-2xl border border-green/20 bg-green/5 px-5 py-4 text-sm text-navy/80">
          <Package size={18} className="mt-0.5 text-green shrink-0" />
          <span>Il kit si compone ordinando separatamente la pompa e gli accessori. Ogni accessorio è a 4 camere (4 sezioni).</span>
        </div>

        {/* Filtri accessori — 4 camere */}
        <div className="mt-8 flex flex-wrap gap-2.5" role="group" aria-label="Filtra gli accessori" data-testid="presso-filters">
          {chips.map((c) => {
            const on = c.id === active;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => selectAcc(c.id)}
                aria-pressed={on}
                data-testid={`presso-filter-${c.id}`}
                className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2 ${
                  on ? "border-green bg-green text-white" : "border-[#E7EDF2] text-navy/70 hover:border-green hover:text-green"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Gruppi accessori filtrati */}
        <div id="presso-kit" className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3 scroll-mt-28">
          {visible.map((g) => (
            <div key={g.id} data-testid={`presso-group-${g.id}`}>
              <h3 className="font-serif text-lg font-bold text-navy">{g.label}</h3>
              <div className="mt-4"><CodeList items={g.items} /></div>
            </div>
          ))}
        </div>

        {/* Specifiche tecniche (sempre visibili) */}
        <div className="mt-14 max-w-xl" data-testid="presso-specifiche">
          <h3 className="font-serif text-lg font-bold text-navy">Specifiche tecniche</h3>
          <dl className="mt-4 overflow-hidden rounded-2xl border border-[#E7EDF2]">
            {SPECIFICHE.map((s, i) => (
              <div key={s.k} className={`flex items-center justify-between gap-4 px-5 py-3.5 ${i % 2 === 0 ? "bg-cloud" : "bg-white"}`}>
                <dt className="text-sm font-medium text-navy/70">{s.k}</dt>
                <dd className="text-sm font-semibold text-navy text-right">{s.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
};

const Pressoterapia = () => (
  <>
    <ProductHero
      theme="navy"
      badge="Pressoterapia Medicale"
      name="Pressoterapia"
      layout="split"
      tagline="Sistemi di pressoterapia per il drenaggio linfatico sequenziale degli arti. Tecnologia medicale al servizio del trattamento decongestivo."
      testid="pressoterapia-hero"
    />

    <section className="py-20 lg:py-28 bg-white" data-testid="presso-device">
      <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="overflow-hidden rounded-[1.75rem] border border-[#E7EDF2] shadow-[0_30px_70px_-30px_rgba(15,25,35,0.22)]">
            <img src={IMG.pressotherapy} alt="Pressoterapia elettronica ADVANCE" className="w-full h-[440px] object-cover" />
          </div>
        </Reveal>
        <div>
          <SectionHeading
            size="cat"
            maxW="max-w-none"
            eyebrow="Modello di punta · marchio LEM"
            title="Pressoterapia elettronica ADVANCE"
            intro="Terapia a compressione sequenziale — 4 uscite. Compressore a 4 uscite con regolazione elettronica."
          />
          <Stagger className="mt-8 grid sm:grid-cols-2 gap-4">
            {SPECS.map((s) => (
              <StaggerItem key={s.label}>
                <div className="rounded-2xl border border-[#E7EDF2] p-5" data-testid={`presso-spec-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-green/10 text-green"><s.icon size={20} /></span>
                  <div className="mt-3 font-serif text-lg font-bold text-navy">{s.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.value}</div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>

    <section className="py-20 lg:py-28 bg-cloud" data-testid="presso-descrizione">
      <div className="container-x max-w-4xl">
        <SectionHeading size="cat" maxW="max-w-2xl" eyebrow="Come funziona" title="Drenaggio sequenziale, programmi A e B." />
        <Reveal delay={0.05}>
          <div className="mt-6 space-y-4 text-muted-foreground leading-relaxed">
            <p>
              Sistema di pressoterapia sequenziale, realizzato appositamente per il drenaggio linfatico ma anche per
              edemi, gambe e braccia gonfie e/o stanche, ritenzione idrica, cellulite e massaggi.
            </p>
            <p>
              Le sezioni che compongono bracciali, gambali e fascia addominale si gonfiano e si sgonfiano
              sequenzialmente grazie ai 2 programmi (A e B), eliminando e riducendo la possibilità di liquido
              stagnante. È possibile deselezionare le sezioni agendo direttamente sul display.
            </p>
            <p>
              La compressione inizia dalla sezione distale e si muove fino alla sezione prossimale, aiutando
              l&apos;incremento del drenaggio del fluido. La pressione del sistema può essere regolata tramite il display
              touch screen.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    <AccessoriSection />

    <section className="py-20 lg:py-28 bg-cloud" data-testid="presso-indicazioni">
      <div className="container-x">
        <SectionHeading size="cat" eyebrow="Indicazioni cliniche" title="Quando è indicata la pressoterapia." />
        <Stagger className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {INDICAZIONI.map((ind) => (
            <StaggerItem key={ind}>
              <div className="flex items-start gap-3 rounded-xl border border-[#E7EDF2] bg-white px-5 py-4">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green/12 text-green"><Check size={12} strokeWidth={3} /></span>
                <span className="text-sm text-navy/80">{ind}</span>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <RequestInfo theme="navy" />
      </div>
    </section>

    <CTASection />
  </>
);

export default Pressoterapia;
